import { useLivekitContext } from "@/context/features/livekit/LivekitRoomContext";
import {
  createLocalScreenTracks,
  LocalAudioTrack,
  LocalVideoTrack,
} from "livekit-client";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
type UseLocalScreenShareResult = {
  localScreenTrack?: LocalVideoTrack;
  localSystemAudioTrack?: LocalAudioTrack;
  requestScreenShare: () => Promise<
    [LocalVideoTrack | undefined, LocalAudioTrack | undefined]
  >;
  stopScreenShare: () => void;
};
export function useLocalScreenShare(): UseLocalScreenShareResult {
  const [localScreenTrack, setLocalScreenTrack] = useState<LocalVideoTrack>();
  const [localSystemAudioTrack, setLocalSystemAudioTrack] =
    useState<LocalAudioTrack>();
  const { livekitContext } = useLivekitContext();
  const room = livekitContext?.room;
  const { t } = useTranslation();
  const requestScreenShare: () => Promise<
    [LocalVideoTrack | undefined, LocalAudioTrack | undefined]
  > = useCallback(async () => {
    try {
      const tracks = await createLocalScreenTracks();
      let video: LocalVideoTrack | undefined = undefined;
      let audio: LocalAudioTrack | undefined = undefined;

      for (const track of tracks) {
        if (track.kind === "video") {
          video = track as LocalVideoTrack;
          setLocalScreenTrack(video);
        } else if (track.kind === "audio") {
          audio = track as LocalAudioTrack;
          setLocalSystemAudioTrack(audio);
        }
      }
      return [video, audio];
    } catch {
      console.error(t("screen_share_request"));
      return [undefined, undefined];
    }
  }, [t]);

  const stopScreenShare = useCallback(() => {
    if (room && localScreenTrack) {
      room.localParticipant.unpublishTrack(localScreenTrack);
      localScreenTrack.stop();
      setLocalScreenTrack(undefined);
    } else if (localScreenTrack) {
      localScreenTrack.stop();
      setLocalScreenTrack(undefined);
    }

    if (room && localSystemAudioTrack) {
      room.localParticipant.unpublishTrack(localSystemAudioTrack);
      localSystemAudioTrack.stop();
      setLocalSystemAudioTrack(undefined);
    } else if (localSystemAudioTrack) {
      localSystemAudioTrack.stop();
      setLocalSystemAudioTrack(undefined);
    }
  }, [room, localScreenTrack, localSystemAudioTrack]);
  return useMemo(
    () =>
      ({
        localScreenTrack,
        localSystemAudioTrack,
        requestScreenShare,
        stopScreenShare,
      }) as const,
    [
      localScreenTrack,
      localSystemAudioTrack,
      requestScreenShare,
      stopScreenShare,
    ]
  );
}
